/**
 * @fileOverview WebViewからアプリにパラメータを送信するための関数を定義
*/

import page from '@/services/Page'
interface WebKitMessageHandler {
  postMessage: (message: string) => void
}

interface WebKitMessageHandlers {
  [key: string]: WebKitMessageHandler
}

interface WebKit {
  messageHandlers: WebKitMessageHandlers
}
interface CustomParameters {
  [key: string]: string
}

interface ButtonEventParams {
  eventName: string // イベント名（ID）
  buttonName: string // ボタン名（key）
  actionName?: string // アクション名（value) 省略時は'-'
  customParameters?: CustomParameters // 今の所店舗IDのみ 例: {'Scode': 'S123456'}
}

/**
 * アプリ向けにWindowインターフェースを拡張
 */
declare global {
  interface Window {
    webkit?: WebKit
    Android?: {
      [key: string]: ((params: string) => void) | undefined
    }
  }
}

// スキップ時に送信するSSコード
const SKIP_SS_CODE = 'S999999'

// アプリトラッキング アクション名
const TRACKING_ACTIONS = {
  NONE: '-',
  TAP: 'TAP',
}

// アプリ（WebView）側で実行するメソッド
const WEBVIEW_METHODS = {
  PV_SET: 'PVSet',
  EVENT_SET: 'EVENTSet',
  MYSS_SET: 'MYSSSet',
}

// イベントトラッキングに送る値
const TRACKING_VALUES = {
  // マップ 初期表示 店舗選択
  INIT_SS_SELECT: {
    eventName: 'MYSSSentakuButton',
    buttonName: 'SYOKAI_MYSSSENTAKU',
    actionName: TRACKING_ACTIONS.TAP,
  },
  // 初期表示 スキップ
  INIT_SKIP: {
    eventName: 'MYSSSentakuSkipButton',
    buttonName: 'MYSSSENTAKU_CANCEL',
    actionName: TRACKING_ACTIONS.TAP,
  },
  // マップ 初期表示 店舗詳細へ移動
  INIT_MOVE_DETAIL: {
    eventName: 'SSSENTAKU_DETAIL',
    buttonName: 'SYOKAI_SSSENTAKU_DETAIL',
    actionName: TRACKING_ACTIONS.TAP,
  },
  // マップ 店舗選択
  MAP_SS_SELECT: {
    eventName: 'MYSSSentakuButton',
    buttonName: 'MYSSSENTAKU',
    actionName: TRACKING_ACTIONS.NONE,
  },
  // マップ 店舗選択（今回だけ）
  MAP_SS_ONCE_SELECT: {
    eventName: 'ICHIGENSSButton',
    buttonName: 'ICHIGENSS_MAP',
    actionName: TRACKING_ACTIONS.TAP,
  },
  // マップ 店舗詳細へ移動
  MAP_MOVE_DETAIL: {
    eventName: 'SSSENTAKU_DETAIL',
    buttonName: 'SSSENTAKU_DETAIL',
    actionName: TRACKING_ACTIONS.TAP,
  },
  // 店舗詳細 店舗選択
  DETAIL_SS_SELECT: {
    eventName: 'MYSSSentakuButton',
    buttonName: 'MYSSSENTAKU',
    actionName: TRACKING_ACTIONS.NONE,
  },
  // 店舗詳細 店舗選択（今回だけ）
  DETAIL_SS_ONCE_SELECT: {
    eventName: 'ICHIGENSSButton',
    buttonName: 'ICHIGENSS_DETAIL',
    actionName: TRACKING_ACTIONS.TAP,
  },
  // 店舗詳細 電話発信
  DETAIL_PHONE_CALL: {
    eventName: 'SSDETAIL_CALLButton',
    buttonName: 'SSDETAIL_CALL',
    actionName: TRACKING_ACTIONS.TAP,
  }
}

// 連続送信の防止用のオブジェクト、同じ値は DEBOUNCE_DELAY 以内に送信しない
const lastCallTracker = new Map<string, { timestamp: number; params: string }>()
const DEBOUNCE_DELAY = 1000 // 1秒

/**
 * アプリ側のメソッドへパラメータを送信する
 * @param {string} params 送信する文字列
 * @param {string} [methodName=WEBVIEW_METHODS.MYSS_SET] アプリ側で実行するメソッド名
 * @returns {boolean} 送信が成功したかどうか
 */
const sendParamsToApp = (params: string, methodName: string = WEBVIEW_METHODS.MYSS_SET): boolean => {

  const callKey = `${methodName}:${params}`
  const now = Date.now()
  const lastCall = lastCallTracker.get(callKey)
  // NOTE: アプリ向けに適切なデバッグフラグがないので仮にアナリティクスの値を参照
  const isDebugMode = storelocator.analytics?.google_analytics?.debug || storelocator.url_params.debug != undefined

  // 直近に送信済みであれば送信しない
  if (lastCall && (now - lastCall.timestamp) < DEBOUNCE_DELAY) {
    return false
  }

  if (isDebugMode) {
    console.debug(`[WebView] アプリに送信開始: ${methodName} | params: ${params}`)
  }

  // 送信する値を記録
  lastCallTracker.set(callKey, { timestamp: now, params })

  try {
    if ('Android' in window) {
      // Android向けのインターフェース呼び出し
      const android = window.Android
      if (android && typeof android[methodName] === 'function') {
        android[methodName](params)
        if (isDebugMode) {
          console.debug(`[WebView] Android送信成功: ${methodName}`)
        }
        return true
      }
    } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers[methodName]) {
      window.webkit.messageHandlers[methodName].postMessage(params)
      if (isDebugMode) {
        console.debug(`[WebView] iOS送信成功: ${methodName}`)
      }
      return true
    }
    // どちらのインターフェースも利用できない場合
    if (isDebugMode) {
      console.warn(`Neither Android nor iOS WebView interface found for '${methodName}'`)      
    }
    return false
  } catch (error) {
    console.error('Error sending message to app:', error)
    return false
  }
}

/**
 * paramsをアプリのイベントトラッキング向けのjson文字列に整形して返す
 */
const createEventJson = (params: ButtonEventParams): string => {
  // console.debug('createEventJson', params)
  const { 
    eventName, 
    buttonName, 
    actionName = '-', 
    customParameters
  } = params

  const eventJson: Record<string, string | object> = {
    'EVENTNAME': eventName,
    [buttonName]: actionName,
  }
  // カスタムパラメータをjsonに追加
  if (customParameters) {
    Object.assign(eventJson, customParameters)
  }
  return JSON.stringify(eventJson)
}


export default {
  /**
   * 店舗選択
   * @param {Object} point Point
   */
  handleSelectedSS: (point: Point) => {
    const ssCode = point.key
    const once = 0
    const frm = storelocator.url_params.frm || ''
    const isPointPage = location.href.includes(page.baseUrls.point)
    const isInitialPage = storelocator.url_params.skip == '1'
    const trackingParams: ButtonEventParams = isInitialPage 
      ? TRACKING_VALUES.INIT_SS_SELECT : isPointPage
      ? TRACKING_VALUES.DETAIL_SS_SELECT
      : TRACKING_VALUES.MAP_SS_SELECT

    if ( isInitialPage) {
      trackingParams.customParameters = { 'tappedMYSSSentakuSSCD': ssCode }
    } else {
      trackingParams.customParameters = { 'Scode': ssCode }
    }
    
    sendParamsToApp(createEventJson(trackingParams), WEBVIEW_METHODS.EVENT_SET)
    sendParamsToApp(`${ssCode},${frm},${once}`)
  },

  /**
   * 今回だけこの店舗のクーポンを使う
   * @param {Object} point Point
   */
  handleOnceSelectedSS: (point: Point) => {
    const ssCode = point.key
    const once = 1
    const frm = storelocator.url_params.frm || ''
    const isPointPage = location.href.includes(page.baseUrls.point)
    const trackingParams: ButtonEventParams = isPointPage
      ? TRACKING_VALUES.DETAIL_SS_ONCE_SELECT
      : TRACKING_VALUES.MAP_SS_ONCE_SELECT
    
    trackingParams.customParameters = { 'Scode': ssCode }

    sendParamsToApp(createEventJson(trackingParams), WEBVIEW_METHODS.EVENT_SET)
    sendParamsToApp(`${ssCode},${frm},${once}`)
  },

  /**
   * SSを登録せずに追加する
   */
  handleSkipSelectSS: () => {
    const ssCode = SKIP_SS_CODE
    const once = 0
    const frm = storelocator.url_params.frm || ''
    const trackingParams: ButtonEventParams = TRACKING_VALUES.INIT_SKIP

    sendParamsToApp(createEventJson(trackingParams), WEBVIEW_METHODS.EVENT_SET)
    sendParamsToApp(`${ssCode},${frm},${once}`)
  },

  /**
   * アプリにイベント送信して、店舗ページに移動する
   */
  handleOpenDetail: (point: Point) => {
    const ssCode = point.key
    // skip=1なら初期設定表示
    const trackingParams: ButtonEventParams = storelocator.url_params.skip == '1'
      ? TRACKING_VALUES.INIT_MOVE_DETAIL
      : TRACKING_VALUES.MAP_MOVE_DETAIL

    trackingParams.customParameters = { 'Scode': ssCode }
    
    // イベントトラッキング送信
    sendParamsToApp(createEventJson(trackingParams), WEBVIEW_METHODS.EVENT_SET)

    // 移動
    window.location.assign(page.getPointUrl(point))
  },

  /**
   * 電話発信イベントをアプリに送信
   */
  handlePhoneCall: (point: Point) => {
    const ssCode = point.key
    const trackingParams: ButtonEventParams = TRACKING_VALUES.DETAIL_PHONE_CALL

    trackingParams.customParameters = { 'Scode': ssCode }
    
    // イベントトラッキング送信
    sendParamsToApp(createEventJson(trackingParams), WEBVIEW_METHODS.EVENT_SET)
    // 通話自体はhtml側で処理
  },

  /**
   * イベントトラッキング送信: 画面遷移
   */
  sendScreenEvent: (screenName: string) => {
    const eventJson = {
      'SHOWPAGE': screenName
    }
    sendParamsToApp(JSON.stringify(eventJson), WEBVIEW_METHODS.PV_SET)
  },
}
